import { useTranslation } from "react-i18next";
import Input from "../Input";
import { useMemo, useState } from "react";
import getIcon from "../../utility/getIcon";
import { Icons } from "../../images/icons";
import { isDecimalInput } from "../../utility/inputValidation";

const ALL_CATEGORIES = "all";

const StockConfigFields = ({
  stockItemId,
  stockLinks,
  stockItems,
  stockCategories = [],
  quantityPerSale,
  outOfStockBehavior,
  onStockLinksChange,
  onStockIdChange,
  onQuantityChange,
  onOutOfStockBehaviorChange,
}) => {
  const { t } = useTranslation("editproduct");
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [stockSearchText, setStockSearchText] = useState("");
  const [selectedStockCategoryId, setSelectedStockCategoryId] = useState(ALL_CATEGORIES);
  const [activeLineIndex, setActiveLineIndex] = useState(null);

  const normalizedStockLinks = useMemo(() => {
    if (Array.isArray(stockLinks)) return stockLinks;
    if (stockItemId) return [{ stockItemId, quantityPerSale }];
    return [];
  }, [quantityPerSale, stockItemId, stockLinks]);

  const stockCategoriesById = useMemo(
    () => new Map(stockCategories.map((category) => [category.id, category])),
    [stockCategories],
  );

  const getStockCategory = (item) => stockCategoriesById.get(item?.categoryId);

  const getStockCategoryIcon = (item) => {
    const stockCategory = getStockCategory(item);
    return stockCategory?.customIcon ? getIcon(null, stockCategory.customIcon) : Icons.others;
  };

  const filteredStockItems = useMemo(() => {
    const normalizedSearch = stockSearchText.trim().toLowerCase();

    return stockItems
      .filter((item) => {
        const matchesCategory =
          selectedStockCategoryId === ALL_CATEGORIES || item.categoryId === selectedStockCategoryId;
        const category = stockCategoriesById.get(item.categoryId);
        const matchesSearch =
          !normalizedSearch ||
          item.name?.toLowerCase().includes(normalizedSearch) ||
          item.id?.toLowerCase().includes(normalizedSearch) ||
          category?.name?.toLowerCase().includes(normalizedSearch);

        return matchesCategory && matchesSearch;
      })
      .sort((firstItem, secondItem) => (firstItem.name || "").localeCompare(secondItem.name || ""));
  }, [selectedStockCategoryId, stockCategoriesById, stockItems, stockSearchText]);

  const syncLegacyProps = (nextLinks) => {
    const firstLink = nextLinks[0];
    if (onStockIdChange) onStockIdChange(firstLink?.stockItemId || "");
    if (onQuantityChange) onQuantityChange(firstLink?.quantityPerSale || "");
  };

  const updateStockLinks = (updater) => {
    const nextLinks = updater(normalizedStockLinks).filter(Boolean);
    if (onStockLinksChange) onStockLinksChange(nextLinks);
    syncLegacyProps(nextLinks);
    if (nextLinks.length <= 0) onOutOfStockBehaviorChange("");
    else if (!outOfStockBehavior) onOutOfStockBehaviorChange("alert");
  };

  const handleQuantityPerSaleChange = (index, value) => {
    if (!isDecimalInput(value)) return;
    updateStockLinks((currentLinks) =>
      currentLinks.map((link, currentIndex) =>
        currentIndex === index ? { ...link, quantityPerSale: value } : link,
      ),
    );
  };

  const openStockPicker = (index) => {
    setActiveLineIndex(index);
    setIsStockModalOpen(true);
  };

  const addStockLink = () => {
    updateStockLinks((currentLinks) => [...currentLinks, { stockItemId: "", quantityPerSale: "" }]);
    openStockPicker(normalizedStockLinks.length);
  };

  const selectStockItem = (stockId) => {
    updateStockLinks((currentLinks) => {
      const nextLinks = [...currentLinks];
      const index = activeLineIndex ?? nextLinks.length;
      nextLinks[index] = {
        ...(nextLinks[index] || {}),
        stockItemId: stockId,
        quantityPerSale: "",
      };
      return nextLinks;
    });
    setIsStockModalOpen(false);
  };

  const removeStockLink = (index) => {
    updateStockLinks((currentLinks) => currentLinks.filter((_, currentIndex) => currentIndex !== index));
  };

  return (
    <>
      <div className="mb-20">
        <label className="form-label __form-label">{t("stock_links_title")}</label>
        <div className="stockLinksList">
          {normalizedStockLinks.length === 0 ? (
            <div className="stockLinksEmpty">{t("stock_links_empty")}</div>
          ) : (
            normalizedStockLinks.map((link, index) => {
              const selectedStockItem = stockItems.find((item) => item.id === link.stockItemId);
              return (
                <div className="stockLinkRow" key={`${link.stockItemId || "new"}-${index}`}>
                  <div className="stockLinkPickerLine">
                    <button
                      type="button"
                      className={`stockPickerButton ${selectedStockItem ? "selected" : ""}`}
                      onClick={() => openStockPicker(index)}
                    >
                      {selectedStockItem ? (
                        <div className="stockPickerButtonContent">
                          <img src={getStockCategoryIcon(selectedStockItem)} alt="" />
                          <div>
                            <p>{selectedStockItem.name}</p>
                            <span>
                              {getStockCategory(selectedStockItem)?.name ||
                                t("stock_category_unknown")}{" "}
                              · {selectedStockItem.unit || "-"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <span>{t("add_stock_item")}</span>
                      )}
                    </button>
                  </div>
                  <div className="stockLinkQuantityLine">
                    <div className="stockQuantityField">
                      <Input
                        label={t("quantity_per_sale")}
                        value={link.quantityPerSale}
                        onChange={(e) => handleQuantityPerSaleChange(index, e.target.value)}
                      />
                    </div>
                    <input
                      className="form-control __form-control stockUnitBox"
                      value={selectedStockItem?.unit || "-"}
                      readOnly
                    />
                    <button
                      type="button"
                      className="stockPickerClearButton"
                      onClick={() => removeStockLink(index)}
                    >
                      {t("remove")}
                    </button>
                  </div>
                </div>
              );
            })
          )}
          <button type="button" className="stockAddLinkButton" onClick={addStockLink}>
            + {t("add_stock_item")}
          </button>
        </div>
      </div>

      {isStockModalOpen && (
        <div
          className="stockPickerModalOverlay"
          role="presentation"
          onMouseDown={() => setIsStockModalOpen(false)}
        >
          <div
            className="stockPickerModal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="stock-picker-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="stockPickerModalHeader">
              <div>
                <h3 id="stock-picker-title">{t("stock_picker_title")}</h3>
                <p>{t("stock_picker_subtitle")}</p>
              </div>
              <button type="button" onClick={() => setIsStockModalOpen(false)} aria-label={t("close")}>
                ×
              </button>
            </div>

            <div className="stockPickerSearch">
              <input
                value={stockSearchText}
                onChange={(event) => setStockSearchText(event.target.value)}
                placeholder={t("stock_picker_search_placeholder")}
                autoFocus
              />
            </div>

            <div className="stockPickerCategories">
              <button
                type="button"
                className={selectedStockCategoryId === ALL_CATEGORIES ? "active" : ""}
                onClick={() => setSelectedStockCategoryId(ALL_CATEGORIES)}
              >
                {t("stock_picker_all_categories")}
              </button>
              {stockCategories.map((category) => (
                <button
                  type="button"
                  key={category.id}
                  className={selectedStockCategoryId === category.id ? "active" : ""}
                  onClick={() => setSelectedStockCategoryId(category.id)}
                >
                  <img
                    src={category.customIcon ? getIcon(null, category.customIcon) : Icons.others}
                    alt=""
                  />
                  {category.name}
                </button>
              ))}
            </div>

            <div className="stockPickerList">
              {filteredStockItems.length === 0 ? (
                <div className="stockPickerEmpty">{t("no_stock_item_found")}</div>
              ) : (
                filteredStockItems.map((item) => {
                  const category = getStockCategory(item);
                  const isSelected = normalizedStockLinks.some((link) => link.stockItemId === item.id);

                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`stockPickerListItem ${isSelected ? "active" : ""}`}
                      onClick={() => selectStockItem(item.id)}
                    >
                      <img src={getStockCategoryIcon(item)} alt="" />
                      <div>
                        <p>{item.name}</p>
                        <span>{category?.name || t("stock_category_unknown")}</span>
                      </div>
                      <div className="stockPickerListItemMeta">
                        <strong>
                          {Number.isFinite(Number(item.onHand)) ? Number(item.onHand).toFixed(2) : "-"}
                        </strong>
                        <span>{item.unit || "-"}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {normalizedStockLinks.length > 0 && (
        <>
          <div className="mb-20">
            <label className="form-label __form-label">{t("out_of_stock_behavior_label")}</label>
            <div className="mb-28 gap-12px d-flex filter-buttons">
              <button
                type="button"
                className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${
                  outOfStockBehavior === "alert" ? "active" : ""
                }`}
                onClick={() => onOutOfStockBehaviorChange("alert")}
              >
                {t("alert_action")}
              </button>
              <button
                type="button"
                className={`themeBtn themeBtnSecondary m-0 w-100 pos-submit-btn ${
                  outOfStockBehavior === "block" ? "active" : ""
                }`}
                onClick={() => onOutOfStockBehaviorChange("block")}
              >
                {t("block_action")}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default StockConfigFields;
